import React, { useState } from 'react';
import { segmentCase, useIsMobile } from '@guildeducationinc/catalog-tools';
import { Typography, Accordion, Button } from '@guildeducationinc/recess';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { Skeleton } from 'src/components/UserFacetSkeletonLoader';
import { trackEvent } from 'src/services/analytics';
import ExpandMore from '@guildeducationinc/recess/icons/ExpandMoreIcon';
import classNames from 'classnames';
import styles from './Concentrations.module.scss';

interface Props {
	program: GetMemberProgramQuery['memberProgram'];
	loading: boolean;
}

export const Concentrations: React.FC<Props> = ({ program, loading }) => {
	const [showMore, setShowMore] = useState(false);
	const isMobile = useIsMobile();

	const concentrations =
		program?.contentItems.filter(item => item.category === 'CONCENTRATION') || [];

	if (concentrations.length === 0) {
		return null;
	}

	const handleClick = () => {
		const buttonText = showMore ? 'show_less' : 'show_more';
		trackEvent('Clickable', {
			tag: `concentrations_${buttonText}`,
			text: `${segmentCase(buttonText)}`,
			type: 'button',
		});
		setShowMore(!showMore);
	};

	const handleOpen = (title: string) => {
		trackEvent('Clickable', {
			tag: 'program_details_concentrations',
			text: `${segmentCase(title)}`,
			type: 'accordion',
		});
	};

	return (
		<section
			aria-label='concentrations'
			className={styles.sectionContainer}
			data-testid='program-detail-concentrations'
			data-title='Concentrations'
			id='detail-concentrations'
			tabIndex={-1}
		>
			<Typography className={styles.header} testid='concentrations-head' variant='h2'>
				Concentrations
			</Typography>
			<Typography
				className={styles.subhead}
				testid='concentrations-subhead'
				variant={isMobile ? 'body1' : 'body2'}
			>
				Customize your program and gain skills based on your interests and career goals.
			</Typography>
			<Skeleton count={3} dataLoading={loading} height={64}>
				<div className={showMore || concentrations.length <= 3 ? null : styles.concentrationsFade}>
					{concentrations
						.map(({ title, body }) => {
							const AccordionTitle = ({ isOpen }) => {
								if (isOpen) {
									handleOpen(title);
								}
								return (
									<Typography testid='concentration-item-title' variant='title3'>
										{title}
									</Typography>
								);
							};

							return (
								<Accordion key={title} testid='concentration-item' title={AccordionTitle}>
									<Typography testid='concentration-item-body' variant='body1'>
										{body}
									</Typography>
								</Accordion>
							);
						})
						.filter((_, index) => (showMore ? true : index < 3))}
				</div>
				<Button
					appearance='ghost'
					aria-expanded={showMore}
					className={styles.showMoreBtn}
					hidden={concentrations.length <= 3}
					icon={
						<ExpandMore className={classNames({ [styles.showMoreIcon]: showMore })} size={24} />
					}
					iconDirection='right'
					onClick={handleClick}
					testid='show-more-button'
				>
					{showMore ? 'Show less' : 'Show more'}
				</Button>
			</Skeleton>
		</section>
	);
};
